import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiSend } from '../lib/api'
import { RichText } from '../lib/rich'
import { useStreamRunner } from '../lib/useStream'
import type { Illustration, Task, Tree } from '../lib/types'

type Reply = { role: 'user' | 'assistant'; content: string }

/**
 * Объяснение темы: приходит потоком сразу при открытии экрана (Каталог сюда
 * переходит напрямую, без промежуточной кнопки — см. Catalog.tsx). Дальше —
 * свободный вопрос по теме тем же движком, что и в боте (`POST /api/chat`):
 * объяснение обычно кончается наводящим вопросом, и ответить на него иначе
 * было нечем.
 *
 * «Решить задачу» здесь идёт через мост теория → практика (mode
 * `theory_topic`, services/theory_bridge.py на сервере): задача берётся по
 * теме листа, а если у экзамена такой темы нет — по подразделу или разделу
 * (правило §2.30).
 */
export default function Theory() {
  const { nodeId } = useParams<{ nodeId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { text, running, error, run } = useStreamRunner()
  const [messages, setMessages] = useState<Reply[]>([])
  const [images, setImages] = useState<Illustration[]>([])
  const [input, setInput] = useState('')
  const [taskError, setTaskError] = useState('')
  // Есть ли у темы задачи: у «Основ физики» моста в практику нет намеренно.
  const [естьЗадачи, setЕстьЗадачи] = useState(false)

  // Название темы и путь к ней. Объяснение идёт 20–30 секунд, и всё это время
  // на экране было одно «Думаю» — без слова о том, какая тема открыта (живой
  // прогон 22 октября 2026). Тот же запрос, что у каталога.
  const { data: узел } = useQuery<Tree>({
    queryKey: ['tree', nodeId, 't'],
    queryFn: () => apiGet(`/api/tree?${new URLSearchParams({ node: nodeId ?? '', module: 't' })}`),
    enabled: !!nodeId,
  })

  // Вопрос ученика и ответ — в поле зрения, как на экране задачи: под длинным
  // объяснением с рисунками ответ вставал ниже края экрана, и казалось, что
  // наставник молчит. Само объяснение не крутим — его читают с начала.
  const конецЛенты = useRef<HTMLDivElement>(null)
  const былоРеплик = useRef(0)
  useEffect(() => {
    const было = былоРеплик.current
    былоРеплик.current = messages.length
    if (messages.length <= 1 || (messages.length <= было && !running)) return
    конецЛенты.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, running])

  // Объяснение запрашивается при открытии и по кнопке повтора: прежде после
  // сбоя («Не удалось объяснить тему») оставалось только перезагрузить страницу.
  const [попытка, setПопытка] = useState(0)
  useEffect(() => {
    if (!nodeId) return
    setTaskError('')
    // Возврат кнопкой «К теории» из задачи показывает то же объяснение и ту же
    // переписку: прежде тема объяснялась заново — другим текстом, ещё 15–20
    // секунд ожидания, а ответ на проверочный вопрос пропадал (живой прогон
    // 25 октября 2026). Заново — только по кнопке повтора.
    const сохранено = попытка === 0 ? прочитатьЭкран(nodeId) : null
    if (сохранено) {
      setMessages(сохранено.messages)
      setImages(сохранено.images)
      setЕстьЗадачи(сохранено.естьЗадачи)
      return
    }
    setMessages([])
    setImages([])
    setЕстьЗадачи(false)
    run('/api/theory/explain', { body: { node_id: nodeId } }, (done) => {
      setImages((done.images as Illustration[]) ?? [])
      setЕстьЗадачи(done.practice !== false)
      setMessages([{ role: 'assistant', content: typeof done.text === 'string' ? done.text : '' }])
    })
  }, [nodeId, run, попытка])

  useEffect(() => {
    if (nodeId && messages.length > 0 && !running) {
      записатьЭкран(nodeId, { messages, images, естьЗадачи })
    }
  }, [nodeId, messages, images, естьЗадачи, running])

  async function спросить() {
    const текст = input.trim()
    if (!текст || running) return
    setInput('')
    // Вопрос — в ленту сразу: ответ идёт до 40 секунд, и без него ученик не
    // видел, ушло ли сообщение (так же устроен экран задачи).
    setMessages((m) => [...m, { role: 'user', content: текст }])
    let ответили = false
    // without_task: вопрос к теме, а не к задаче, брошенной до ухода в теорию —
    // иначе сервер сверял бы его с её ответом и записывал неверной попыткой.
    await run('/api/chat', { body: { text: текст, without_task: true } }, (done) => {
      ответили = true
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: typeof done.text === 'string' ? done.text : '' },
      ])
    })
    if (!ответили) {
      // Сбой — вопрос возвращается в поле: отправить его снова одной кнопкой.
      setMessages((m) => m.slice(0, -1))
      setInput(текст)
    }
  }

  async function решать() {
    setTaskError('')
    try {
      const task = await apiSend<Task>('/api/tasks/next', 'POST', {
        mode: 'theory_topic',
        node_id: nodeId,
      })
      queryClient.setQueryData(['task', 'current'], { task })
      navigate('/task')
    } catch (e) {
      setTaskError(
        e instanceof ApiError ? e.message : 'Не получилось взять задачу.',
      )
    }
  }

  return (
    <>
      <button className="btn" onClick={() => navigate(-1)} style={{ marginBottom: 12 }}>
        ⬅️ Назад
      </button>

      {узел?.node?.title && <h1>{узел.node.title}</h1>}
      {узел?.breadcrumbs && <div className="crumbs">{узел.breadcrumbs}</div>}

      <div className="dialogue">
        {messages.map((m, i) => (
          <Fragment key={i}>
            <div className={`msg ${m.role === 'user' ? 'msg-user' : 'msg-ai'}`}>
              <RichText text={m.content} plain={m.role === 'user'} />
            </div>
            {/* Рисунки — сразу под объяснением, к которому они подобраны: оно
                ссылается на них («на рисунке ниже»), а под всей перепиской после
                пары вопросов их приходилось искать. */}
            {i === 0 && images.map((img) => <TheoryFigure key={img.url} img={img} />)}
          </Fragment>
        ))}

        {running && (
          <div className="msg msg-ai">
            {text ? (
              <>
                <RichText text={text} />
                <span className="muted"> ▌</span>
              </>
            ) : (
              <span className="muted dots">Думаю</span>
            )}
          </div>
        )}
        <div ref={конецЛенты} />
      </div>

      {error && <div className="error">{error}</div>}

      {/* Объяснения нет — повтор прямо здесь. Сбой вопроса по теме повторяется
          отправкой: вопрос возвращается в поле ввода (спросить). */}
      {error && !running && messages.length === 0 && (
        <button
          className="btn btn-primary btn-block"
          onClick={() => setПопытка((n) => n + 1)}
          style={{ margin: '12px 0' }}
        >
          🔄 Попробовать ещё раз
        </button>
      )}

      {taskError && <div className="error">{taskError}</div>}

      <div className="composer">
        {/* «Решить задачу» — у поля ввода, на виду всё время. Прежде кнопка
            стояла в самом низу, под объяснением и рисунками, и до неё надо
            было долистать (живой прогон 22 октября 2026). Подсказка в поле —
            «Ответь или спроси»: объяснение кончается вопросом. */}
        {messages.length > 0 && естьЗадачи && (
          <div className="row-wrap">
            <button className="btn btn-primary" onClick={решать} disabled={running}>
              📝 Решить задачу
            </button>
          </div>
        )}
        <div className="composer-row">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                спросить()
              }
            }}
            placeholder="Ответь или спроси…"
            rows={1}
            maxLength={1000}
          />
          <button className="btn btn-primary" onClick={спросить} disabled={running || !input.trim()}>
            ➤
          </button>
        </div>
      </div>
    </>
  )
}

type ЭкранТеории = { messages: Reply[]; images: Illustration[]; естьЗадачи: boolean }

// Память вкладки, а не сервер: это удобство одного ученика на одном
// устройстве. Хранилище бывает недоступно (приватный режим) — тогда тема
// просто объясняется заново, как раньше.
const ключЭкрана = (nodeId: string) => `theory:${nodeId}`

function прочитатьЭкран(nodeId: string): ЭкранТеории | null {
  try {
    const raw = sessionStorage.getItem(ключЭкрана(nodeId))
    const data = raw ? (JSON.parse(raw) as ЭкранТеории) : null
    return data && Array.isArray(data.messages) && data.messages.length > 0 ? data : null
  } catch {
    return null
  }
}

function записатьЭкран(nodeId: string, экран: ЭкранТеории) {
  try {
    sessionStorage.setItem(ключЭкрана(nodeId), JSON.stringify(экран))
  } catch {
    // Переполнено или запрещено — восстановления не будет, только и всего.
  }
}

function TheoryFigure({ img }: { img: Illustration }) {
  return (
    <figure className="card msg-figure" style={{ margin: '12px 0' }}>
      <img src={img.url} alt={img.caption ?? 'Иллюстрация из учебника'} loading="lazy" />
      <figcaption className="small muted">
        📘 Иллюстрация из учебника
        {/* Подпись с формулами рисует KaTeX — как и текст объяснения. */}
        {img.caption && (
          <div className="figure-caption">
            <RichText text={img.caption} />
          </div>
        )}
      </figcaption>
    </figure>
  )
}
